import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { useApp } from "@/context/AppContext";
import { PageHeader, Panel, Loading, Empty, Field, Th, Td, TableWrap, Money, NativeSelect } from "@/components/kit";
import { ImportButton } from "@/components/ImportButton";
import { errMsg } from "@/lib/format";

const ItemDialog = ({ open, onClose, initial, onSaved, rates }) => {
  const { api, t } = useApp();
  const [f, setF] = useState({});
  useEffect(() => { setF(initial?.id ? initial : { kind: "product", unit: "U", vat_rate: 19, price_ht: 0, track_stock: false, min_stock: 0 }); }, [initial, open]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async () => {
    try {
      const body = { ...f, price_ht: +f.price_ht || 0, purchase_price: +f.purchase_price || 0, vat_rate: +f.vat_rate, min_stock: +f.min_stock || 0 };
      if (initial?.id) await api.put(`/items/${initial.id}`, body); else await api.post("/items", body);
      onSaved();
      onClose();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl bg-white">
        <DialogHeader><DialogTitle>{initial?.id ? t("edit") : t("new_item")}</DialogTitle></DialogHeader>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label={t("code")}><Input data-testid="item-code" value={f.code || ""} onChange={set("code")} /></Field>
          <Field label={t("kind")}><NativeSelect data-testid="item-kind" value={f.kind} onChange={set("kind")}><option value="product">{t("product")}</option><option value="service">{t("service")}</option></NativeSelect></Field>
          <Field label={t("name_fr")}><Input data-testid="item-name-fr" value={f.name_fr || ""} onChange={set("name_fr")} /></Field>
          <Field label={t("name_ar")}><Input data-testid="item-name-ar" dir="rtl" value={f.name_ar || ""} onChange={set("name_ar")} /></Field>
          <Field label={t("unit")}><Input data-testid="item-unit" value={f.unit || ""} onChange={set("unit")} /></Field>
          <Field label={t("price_ht")}><Input data-testid="item-price" type="number" step="0.001" value={f.price_ht} onChange={set("price_ht")} /></Field>
          <Field label={t("vat_rate")}><NativeSelect data-testid="item-vat" value={f.vat_rate} onChange={set("vat_rate")}>{rates.map((r) => <option key={r} value={r}>{`${r} %`}</option>)}</NativeSelect></Field>
          <Field label={t("purchase_price")}><Input data-testid="item-purchase-price" type="number" step="0.001" value={f.purchase_price || ""} onChange={set("purchase_price")} /></Field>
          <Field label={t("account")}><Input data-testid="item-account" placeholder="707" value={f.account || ""} onChange={set("account")} /></Field>
          <Field label={t("min_stock")}><Input data-testid="item-min-stock" type="number" value={f.min_stock} onChange={set("min_stock")} /></Field>
          <label className="flex items-center gap-2 text-sm"><Switch data-testid="item-track-stock" checked={!!f.track_stock} onCheckedChange={(v) => setF({ ...f, track_stock: v })} />{t("track_stock")}</label>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>{t("cancel")}</Button><Button onClick={save} data-testid="item-save">{t("save")}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default function Items() {
  const { api, t, can, lang } = useApp();
  const [rows, setRows] = useState(null);
  const [rates, setRates] = useState([19, 13, 7, 0]);
  const [q, setQ] = useState("");
  const [edit, setEdit] = useState(null);
  const load = useCallback(() => api.get("/items", { params: { q } }).then((r) => setRows(r.data)), [api, q]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/fiscal/current").then((r) => setRates(r.data.vat_rates)); }, [api]);
  const rw = can("items", true);
  const del = async (i) => {
    if (!window.confirm(`${t("delete")} ${i.code} ?`)) return;
    try { await api.delete(`/items/${i.id}`); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <>
      <PageHeader eyebrow={t("items")} title={t("items")}>
        <Input placeholder={t("search")} value={q} onChange={(e) => setQ(e.target.value)} className="w-56" data-testid="item-search" />
        {rw && <ImportButton entity="items" onDone={load} />}
        {rw && <Button onClick={() => setEdit({})} data-testid="item-new-btn"><Plus className="h-4 w-4 me-1" />{t("new_item")}</Button>}
      </PageHeader>
      <Panel>
        {!rows ? <Loading /> : rows.length === 0 ? <Empty /> : (
          <TableWrap>
            <thead className="border-b border-slate-100"><tr><Th>{t("code")}</Th><Th>{t("description")}</Th><Th>{t("unit")}</Th><Th className="text-end">{t("price_ht")}</Th><Th>{t("vat_rate")}</Th><Th>{t("account")}</Th><Th className="text-end">{t("stock")}</Th><Th /></tr></thead>
            <tbody>
              {rows.map((i) => (
                <tr key={i.id} className="border-b border-slate-50 hover:bg-slate-50/70" data-testid={`item-row-${i.code}`}>
                  <Td className="num text-xs">{i.code}</Td><Td className="font-medium">{lang === "ar" && i.name_ar ? i.name_ar : i.name_fr}</Td><Td>{i.unit}</Td>
                  <Td className="text-end"><Money v={i.price_ht} /></Td><Td>{i.vat_rate} %</Td><Td className="num text-xs">{i.account || "—"}</Td>
                  <Td className="text-end num">{i.track_stock ? i.stock_total : "—"}</Td>
                  <Td className="text-end whitespace-nowrap">
                    {rw && <Button variant="ghost" size="icon" onClick={() => setEdit(i)} data-testid={`item-edit-${i.code}`}><Pencil className="h-4 w-4" /></Button>}
                    {rw && <Button variant="ghost" size="icon" onClick={() => del(i)} data-testid={`item-delete-${i.code}`}><Trash2 className="h-4 w-4 text-red-600" /></Button>}
                  </Td>
                </tr>
              ))}
            </tbody>
          </TableWrap>
        )}
      </Panel>
      <ItemDialog open={!!edit} onClose={() => setEdit(null)} initial={edit} onSaved={load} rates={rates} />
    </>
  );
}
